import { enableProdMode } from '@angular/core';
import { TicketDescriptionDisplayMode } from '../enum/ticket-description-display-mode.enum';

export abstract class ConfigService {
  public static setEnvironment(config, environment) {
    if (!config) return;

    environment.siteUrl = config.siteUrl ?? environment.siteUrl;
    environment.apiUrl = config.apiUrl ?? environment.apiUrl;
    environment.backUrl = config.backUrl ?? config.homepageUrl ?? environment.backUrl;
    environment.virtualCinema = config.virtualCinema ?? environment.virtualCinema;
    environment.activeLogger = config.activeLogger ?? environment.activeLogger;
    environment.redirectUrlFromScreeningPage = config.redirectUrlFromScreeningPage ?? environment.redirectUrlFromScreeningPage;

    if (config.constants) {
      environment.constants.clearMessageOnPageChange = config.constants.clearMessageOnPageChange ?? environment.constants.clearMessageOnPageChange;
      environment.constants.saveCurrentUrlToCookie = config.constants.saveCurrentUrlToCookie ?? environment.constants.saveCurrentUrlToCookie;
      environment.constants.moviePosterPlaceholder = config.constants.moviePosterPlaceholder ?? environment.constants.moviePosterPlaceholder;
      if (config.constants['baseMoviePoster']) {
        //backward compatibility
        environment.constants.moviePosterPlaceholder = config.constants['baseMoviePoster'];
      }
      environment.constants.moviePicturePlaceholder = config.constants.moviePicturePlaceholder ?? environment.constants.moviePicturePlaceholder;
      environment.constants.moviePosterPlaceholderOnError =
        config.constants.moviePosterPlaceholderOnError ?? environment.constants.moviePosterPlaceholderOnError;
      if (config.constants['baseMoviePosterOnError']) {
        //backward compatibility
        environment.constants.moviePosterPlaceholderOnError = config.constants['baseMoviePosterOnError'];
      }
      environment.constants.currency = config.constants.currency ?? environment.constants.currency;
      environment.constants.country = config.constants.country ?? environment.constants.country;
      environment.constants.countdownStartInit = config.constants.countdownStartInit ?? environment.constants.countdownStartInit;
      environment.constants.deleteOrderOnWindowUnload = config.constants.deleteOrderOnWindowUnload ?? false;
      environment.constants.paymentTestMode = config.constants.paymentTestMode ?? environment.constants.paymentTestMode;
      environment.constants.paymentDebug = config.constants.paymentDebug ?? environment.constants.paymentDebug;
      environment.constants.defaultCinemaId = config.constants.defaultCinemaId ?? environment.constants.defaultCinemaId;
      environment.constants.defaultRegionId = config.constants.defaultRegionId ?? environment.constants.defaultRegionId;
      environment.constants.voucherSaleEnabled = config.constants.voucherSaleEnabled ?? environment.constants.voucherSaleEnabled;
      environment.constants.recaptcha = config.constants.recaptcha ?? environment.constants.recaptcha;
      environment.constants.paymentRedirectUrl = config.constants.paymentRedirectUrl ?? environment.constants.paymentRedirectUrl;
      if (config.constants.language) {
        environment.constants.language.default = config.constants.language.default ?? environment.constants.language.default;
        environment.constants.language.available = config.constants.language.available ?? environment.constants.language.available;
        environment.constants.language.cultureMap = config.constants.language.cultureMap ?? environment.constants.language.cultureMap;
        environment.constants.language.useLangAsUrlParam = config.constants.language.useLangAsUrlParam ?? environment.constants.language.useLangAsUrlParam;

        if (config.constants.language.displaySwitcher !== null) {
          environment.constants.language.displaySwitcher = config.constants.language.displaySwitcher;
        }
      }
      environment.constants.phoneProviderNumbers = config.constants.phoneProviderNumbers ?? environment.constants.phoneProviderNumbers;
      environment.constants.refundAllowTimeBeforeScreening =
        config.constants.refundAllowTimeBeforeScreening ?? environment.constants.refundAllowTimeBeforeScreening;
      if (config.constants['skip2fa']) {
        environment.constants['skip2fa'] = config.constants['skip2fa'];
      }
      if (config.constants['userDataChecklist']) {
        environment.constants['userDataChecklist'] = config.constants['userDataChecklist'];
      }
      if (config.constants['maxSelectedTickets']) {
        environment.constants['maxSelectedTickets'] = config.constants['maxSelectedTickets'];
      }

      if (config.constants['showCinemaSwitcher'] === false) {
        environment.constants['showCinemaSwitcher'] = false;
      } else {
        environment.constants['showCinemaSwitcher'] = true;
      }

      environment.constants.reservationTimeControlMode = config.constants.reservationTimeControlMode ?? environment.constants.reservationTimeControlMode;
      environment.constants.enableFB = config.constants.enableFB ?? environment.constants.enableFB;
      environment.constants.enableECommerce = config.constants.enableECommerce ?? environment.constants.enableECommerce;
      environment.constants.enableTicketsDescription = this.normalizeTicketDescriptionMode(
        config.constants.enableTicketsDescription,
        environment.constants.enableTicketsDescription
      );

      environment.constants.taxLimitAmount = config.constants.taxLimitAmount ?? environment.constants.taxLimitAmount;
    }

    if (config.paymentManager) {
      this.setPaymentManagerConfig(config.paymentManager, environment);
    }

    if (config.tagManager) {
      environment.tagManager.providers = config.tagManager.providers ?? environment.tagManager.providers;
    }

    if (config.screen) {
      environment.screen.enableSeatTooltip = config.screen.enableSeatTooltip ?? environment.screen.enableSeatTooltip;
      environment.screen.showPopupOnSelectedSeat = config.screen.showPopupOnSelectedSeat ?? environment.screen.showPopupOnSelectedSeat;
      environment.screen.reduceRequestsForScreeningItems = config.screen.reduceRequestsForScreeningItems ?? environment.screen.reduceRequestsForScreeningItems;
      environment.screen.reduceRequestsForTicket = config.screen.reduceRequestsForTicket ?? environment.screen.reduceRequestsForTicket;
      environment.screen.periodBetweenRequestsInScreen = config.screen.periodBetweenRequestsInScreen ?? environment.screen.periodBetweenRequestsInScreen;
      environment.screen.removeOrderWhenUserLeavePage = config.screen.removeOrderWhenUserLeavePage ?? environment.screen.removeOrderWhenUserLeavePage;
    }

    if (config.pages) {
      if (config.pages.screening) {
        environment.pages.screening.defaultStartDate = config.pages.screening.defaultStartDate ?? environment.pages.screening.defaultStartDate;
      }

      if (environment.pages['mvoucher'] && config.pages.mvoucher) {
        environment.pages['mvoucher'].bundles = config.pages.mvoucher.bundles ?? environment.pages['mvoucher'].bundles;
        environment.pages['mvoucher'].timer = config.pages.mvoucher.timer ?? environment.pages['mvoucher'].timer;
      }

      environment.pages.screen.legend.asPopup = config.pages.screen?.legend?.asPopup ?? environment.pages.screen.legend.asPopup;
    }

    if (config['scripts']) {
      environment['scripts'] = Object.assign(environment['scripts'] ?? {}, config['scripts']);
    }

    if (config['brightcove']) {
      environment['brightcove'] = Object.assign(environment['brightcove'] ?? {}, config['brightcove']);
    }

    if (config['cms']) {
      if (config['cms']['apiUrl']) {
        environment['cms']['apiUrl'] = config['cms']['apiUrl'];
      }
      if (config['cms']['params']) {
        environment['cms']['params'] = Object.assign(environment['cms']['params'] ?? {}, config['cms']['params']);
      }
      if (config['cms']['routes']) {
        environment['cms']['routes'] = Object.assign(environment['cms']['routes'] ?? {}, config['cms']['routes']);
      }
    }

    if (config['loader']) {
      this.setLoaderConfig(config['loader'], environment);
    }

    if (config['externalAuthProviders']) {
      this.setExternalAuthProvidersConfig(config['externalAuthProviders'], environment);
    }

    if (config['postprocesorUrl']) {
      environment['postprocesorUrl'] = config['postprocesorUrl'];
    }

    if (config['voucherShop']) {
      this.setVoucherShopConfig(config['voucherShop'], environment);
    }

    if (config['additionalNotification']) {
      if (config['additionalNotification']['requiredTicketId']) {
        environment['additionalNotification']['requiredTicketId'] = config['additionalNotification']['requiredTicketId'];
      }
      if (config['additionalNotification']['messages']) {
        environment['additionalNotification']['messages'] = config['additionalNotification']['messages'];
      }
    }

    if (environment['virtualPass'] && config['virtualPass']) {
      environment['virtualPass'] = config['virtualPass'];
    }

    if (environment['placeholder']) {
      environment['placeholder']['picture'] = config.placeholder?.picture ?? config.constants?.moviePicturePlaceholder ?? environment['placeholder']['picture'];
      environment['placeholder']['poster'] = config.placeholder?.poster ?? config.constants?.moviePosterPlaceholder ?? environment['placeholder']['poster'];
      environment['placeholder']['square'] = config.placeholder?.square ?? environment['placeholder']['square'];
      environment['placeholder']['card'] = config.placeholder?.card ?? environment['placeholder']['card'];
      environment['placeholder']['default'] = config.placeholder?.card ?? environment['placeholder']['default'];
    }

    environment['logUrl'] = config.logUrl;

    if (environment['session'] && config['session']) {
      environment['session'] = config['session'];
    }

    //The use of Object.assign overwrites the defined parameters !!!
    //Object.assign(environment, config);
    if (config.externalFlagsApiPath) {
      environment.externalFlagsApiPath = config.externalFlagsApiPath;
    }

    if (config.flagImageExtension !== undefined) {
      environment.flagImageExtension = config.flagImageExtension;
    }

    if (environment.production) {
      enableProdMode();

      if (!environment.activeLogger) {
        window.console.log = function () {}; // disable any console.log debugging statements in production mode
        window.console.warn = function () {}; // disable any console.warn debugging statements in production mode
        // window.console.error = function () { };
      }
    }
  }

  private static setExternalAuthProvidersConfig(authConfig, environment) {
    if (!environment['externalAuthProviders']) {
      environment['externalAuthProviders'] = {
        google: {
          clientId: ''
        },
        facebook: {
          clientId: '',
          supportedLocales: []
        },
        apple: {
          clientId: '',
          supportedLocales: []
        }
      };
    }

    // Konfiguracja Google
    if (authConfig.google) {
      if (!environment['externalAuthProviders'].google) {
        environment['externalAuthProviders'].google = { clientId: '' };
      }
      if (authConfig.google.clientId) {
        environment['externalAuthProviders'].google.clientId = authConfig.google.clientId;
      }
    }

    // Konfiguracja Facebook
    if (authConfig.facebook) {
      if (!environment['externalAuthProviders'].facebook) {
        environment['externalAuthProviders'].facebook = {
          clientId: '',
          supportedLocales: []
        };
      }
      if (authConfig.facebook.clientId) {
        environment['externalAuthProviders'].facebook.clientId = authConfig.facebook.clientId;
      }
      if (authConfig.facebook.supportedLocales) {
        environment['externalAuthProviders'].facebook.supportedLocales = [...authConfig.facebook.supportedLocales];
      }
    }

    // Konfiguracja Apple
    if (authConfig.apple) {
      if (!environment['externalAuthProviders'].apple) {
        environment['externalAuthProviders'].apple = {
          clientId: '',
          supportedLocales: []
        };
      }
      if (authConfig.apple.clientId) {
        environment['externalAuthProviders'].apple.clientId = authConfig.apple.clientId;
      }
      if (authConfig.apple.supportedLocales) {
        environment['externalAuthProviders'].apple.supportedLocales = [...authConfig.apple.supportedLocales];
      }
    }
  }

  private static setPaymentManagerConfig(paymentManagerConfig, environment) {
    if (!environment.paymentManager) {
      environment.paymentManager = {};
    }

    environment.paymentManager = {
      ...environment.paymentManager,
      ...paymentManagerConfig
    };

    if (paymentManagerConfig?.przelewy24) {
      const existingP24Config = environment.paymentManager?.przelewy24 ?? {};
      const incomingP24Config = paymentManagerConfig.przelewy24;

      environment.paymentManager.przelewy24 = {
        ...existingP24Config,
        ...incomingP24Config
      };
    }
  }

  private static setLoaderConfig(loaderConfig, environment) {
    if (!environment['loader']) {
      environment['loader'] = {
        backdropBorderRadius: null,
        backdropBackgroundColour: null,
        fullScreenBackdrop: null,
        animationType: 'custom',
        primaryColour: null,
        secondaryColour: null,
        tertiaryColour: null,
        style: null,
        size: null
      };
    }

    if (typeof loaderConfig.backdropBorderRadius !== 'undefined') {
      environment['loader'].backdropBorderRadius = loaderConfig.backdropBorderRadius;
    }

    if (typeof loaderConfig.backdropBackgroundColour !== 'undefined') {
      environment['loader'].backdropBackgroundColour = loaderConfig.backdropBackgroundColour;
    }

    if (typeof loaderConfig.fullScreenBackdrop !== 'undefined') {
      environment['loader'].fullScreenBackdrop = loaderConfig.fullScreenBackdrop;
    }

    if (loaderConfig.animationType) {
      environment['loader'].animationType = loaderConfig.animationType;
    }

    if (typeof loaderConfig.primaryColour !== 'undefined') {
      environment['loader'].primaryColour = loaderConfig.primaryColour;
    }

    if (typeof loaderConfig.secondaryColour !== 'undefined') {
      environment['loader'].secondaryColour = loaderConfig.secondaryColour;
    }

    if (typeof loaderConfig.tertiaryColour !== 'undefined') {
      environment['loader'].tertiaryColour = loaderConfig.tertiaryColour;
    }

    if (typeof loaderConfig.style !== 'undefined') {
      environment['loader'].style = loaderConfig.style;
    }

    if (typeof loaderConfig.size !== 'undefined') {
      environment['loader'].size = loaderConfig.size;
    }

    if (loaderConfig.lottie) {
      environment['loader'].lottie = {
        path: loaderConfig.lottie.path
      };
    }
  }

  private static setVoucherShopConfig(voucherConfig, environment) {
    if (!environment['voucherShop']) {
      environment['voucherShop'] = {
        enable: false,
        imagePriority: [],
        items: [],
        activeTheme: 'default',
        themes: [],
        displayableOptionalAgreementIds: []
      };
    }

    if (typeof voucherConfig.enable !== 'undefined') {
      environment['voucherShop'].enable = voucherConfig.enable;
    }

    if (voucherConfig.imagePriority) {
      environment['voucherShop'].imagePriority = [...voucherConfig.imagePriority];
    }

    if (voucherConfig.items) {
      environment['voucherShop'].items = voucherConfig.items.map((item) => ({
        key: item.key,
        id: Array.isArray(item.id) ? [...item.id] : item.id,
        image: item.image
      }));
    }

    if (voucherConfig.activeTheme) {
      environment['voucherShop'].activeTheme = voucherConfig.activeTheme;
    }

    if (voucherConfig.themes) {
      if (!environment['voucherShop'].themes) {
        environment['voucherShop'].themes = [];
      }

      voucherConfig.themes.forEach((newTheme) => {
        const existingThemeIndex = environment['voucherShop'].themes.findIndex((theme) => theme.key === newTheme.key);

        if (existingThemeIndex !== -1) {
          environment['voucherShop'].themes[existingThemeIndex] = {
            key: newTheme.key,
            bgColor: newTheme.bgColor,
            bgImage: newTheme.bgImage
          };
        } else {
          environment['voucherShop'].themes.push({
            key: newTheme.key,
            bgColor: newTheme.bgColor,
            bgImage: newTheme.bgImage
          });
        }
      });
    }

    if (voucherConfig.displayableOptionalAgreementIds) {
      environment['voucherShop'].displayableOptionalAgreementIds = voucherConfig.displayableOptionalAgreementIds;
    }

    if (voucherConfig.validityPeriod) {
      environment['voucherShop'].validityPeriod = voucherConfig.validityPeriod;
    }
  }

  private static normalizeTicketDescriptionMode(
    configValue: TicketDescriptionDisplayMode | boolean | null | undefined,
    envValue: TicketDescriptionDisplayMode | boolean | null | undefined
  ): TicketDescriptionDisplayMode {
    const value = configValue ?? envValue;

    if (value === true) {
      return TicketDescriptionDisplayMode.IconOnly;
    }

    if (value === false || value === null || value === undefined) {
      return TicketDescriptionDisplayMode.Disabled;
    }

    if (value === TicketDescriptionDisplayMode.IconOnly || value === TicketDescriptionDisplayMode.DescriptionOnly) {
      return value;
    }

    return TicketDescriptionDisplayMode.Disabled;
  }
}
